import { useEffect, useRef, useState } from 'react';

/**
 * Animated counter using requestAnimationFrame with cubic ease-out.
 * Easing: t => 1 - (1 - t)^3
 *
 * @param {number} end       — target value
 * @param {number} duration  — animation duration in ms (default 2000)
 * @param {boolean} trigger  — starts animation when true
 * @param {string} prefix    — string prepended to display value (e.g. "₹")
 * @param {string} suffix    — string appended to display value (e.g. "+")
 */
export default function useCountUp(end, duration = 2000, trigger = false, prefix = '', suffix = '') {
  const [value, setValue] = useState(0);
  const rafRef = useRef(null);
  const startTimeRef = useRef(null);
  const hasRun = useRef(false);

  useEffect(() => {
    if (!trigger || hasRun.current) return;
    hasRun.current = true;

    const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

    const animate = (timestamp) => {
      if (!startTimeRef.current) startTimeRef.current = timestamp;
      const elapsed = timestamp - startTimeRef.current;
      const progress = Math.min(elapsed / duration, 1);
      const easedProgress = easeOutCubic(progress);

      setValue(Math.round(easedProgress * end));

      if (progress < 1) {
        rafRef.current = requestAnimationFrame(animate);
      }
    };

    rafRef.current = requestAnimationFrame(animate);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [trigger, end, duration]);

  const display = `${prefix}${value.toLocaleString()}${suffix}`;

  return { value, display };
}
